/**
 * pSEO Slug Parser
 * Converts URL slugs into filter parameters for programmatic SEO
 *
 * Example URLs:
 * /rent/dublin/pet-friendly
 * /rent/cork/studio/1000-1500
 * /sale/galway/3-bed
 * /rent/dublin-4/first-time-buyer
 */

import { SearchParams } from '../../pages/browse-listings/BrowseListingsPage';
import {
  PropertyType,
  ListingIntent,
} from '../../components/property/property-card/PropertyCardTypes';

/**
 * Amenity slug mappings
 * Maps URL-friendly slugs to actual amenity names
 */
const amenitySlugs: Record<string, string> = {
  'pet-friendly': 'Pet Friendly',
  wifi: 'Wi-Fi',
  parking: 'Parking',
  gym: 'Gym',
  garden: 'Garden',
  balcony: 'Balcony',
  heating: 'Heating',
  laundry: 'Laundry',
  furnished: 'Furnished',
  pool: 'Pool',
  'air-conditioning': 'Air Conditioning',
  'wheelchair-accessible': 'Wheelchair Accessible',
};

/**
 * Property type slug mappings
 */
const propertyTypeSlugs: Record<string, PropertyType> = {
  apartment: 'Apartment',
  apt: 'Apartment',
  room: 'Room',
  house: 'House',
  studio: 'Studio',
  duplex: 'Duplex',
  townhouse: 'Townhouse',
};

/**
 * Special intent mappings
 */
const intentSlugs: Record<string, string> = {
  'first-time-buyer': 'first-time-buyer',
  buy: 'sale',
  rent: 'rent',
  share: 'share',
  sale: 'sale',
};

/**
 * Main slug parser function
 * Converts URL segments to SearchParams
 */
export const parseUrlSlugs = (pathSegments: string[]): Partial<SearchParams> => {
  const params: Partial<SearchParams> = {
    location: '',
    minPrice: 0,
    maxPrice: 0,
    bedrooms: 0,
    bathrooms: 0,
    propertyType: 'Any' as const,
    berRating: 'Any',
    listingIntent: 'Any' as const,
    availableFrom: null,
  };

  if (!pathSegments || pathSegments.length === 0) return params;

  let index = 0;

  // First segment: listing intent (rent, sale, share)
  if (index < pathSegments.length && intentSlugs[pathSegments[index]]) {
    const intentSlug = pathSegments[index].toLowerCase();
    params.listingIntent = (
      intentSlugs[intentSlug] === 'sale' ? 'sale' : intentSlugs[intentSlug]
    ) as ListingIntent | 'Any';
    index++;
  }

  // Next: location (e.g., "dublin", "dublin-4", "cork")
  if (index < pathSegments.length) {
    const locationSlug = pathSegments[index];
    // Convert slug to proper location format
    params.location = convertLocationSlug(locationSlug);
    index++;
  }

  // Process remaining segments
  while (index < pathSegments.length) {
    const slug = pathSegments[index].toLowerCase();

    // Check for price range (e.g., "1000-1500")
    if (slug.includes('-') && /^\d+-\d+$/.test(slug)) {
      const [minStr, maxStr] = slug.split('-');
      params.minPrice = Math.max(params.minPrice ?? 0, parseInt(minStr, 10));
      params.maxPrice = Math.max(params.maxPrice ?? 0, parseInt(maxStr, 10));
      index++;
      continue;
    }

    // Check for upper-bound price like "under-1500"
    if (slug.startsWith('under-') && /^under-\d+$/.test(slug)) {
      const maxStr = slug.replace('under-', '');
      params.minPrice = params.minPrice ?? 0;
      params.maxPrice = Math.max(params.maxPrice ?? 0, parseInt(maxStr, 10));
      index++;
      continue;
    }

    // Check for bedrooms (e.g., "3-bed", "2-bed")
    if (slug.includes('bed')) {
      const bedroomMatch = slug.match(/^(\d+)-?beds?$/);
      if (bedroomMatch) {
        params.bedrooms = parseInt(bedroomMatch[1], 10);
        index++;
        continue;
      }
    }

    // Check for bathrooms (e.g., "2-bath")
    if (slug.includes('bath')) {
      const bathroomMatch = slug.match(/^(\d+)-?baths?$/);
      if (bathroomMatch) {
        params.bathrooms = parseInt(bathroomMatch[1], 10);
        index++;
        continue;
      }
    }

    // Check for property type
    if (propertyTypeSlugs[slug]) {
      params.propertyType = propertyTypeSlugs[slug];
      index++;
      continue;
    }

    // Check for amenities
    if (amenitySlugs[slug]) {
      // Store as a filtered amenity (we'll need to handle this separately)
      index++;
      continue;
    }

    // Check for BER rating
    if (/^ber-[a-g][1-3]?$/i.test(slug)) {
      const berMatch = slug.match(/^ber-([a-g][1-3]?)$/i);
      if (berMatch) {
        params.berRating = berMatch[1].toUpperCase();
        index++;
        continue;
      }
    }

    index++;
  }

  return params;
};

/**
 * Convert location slug to proper format
 * E.g., "dublin-4" → "Dublin 4", "dublin" → "Dublin"
 */
const convertLocationSlug = (slug: string): string => {
  const parts = slug.split('-');
  return parts
    .map((part) => {
      // Check if it's a number (postcode digit)
      if (/^\d+$/.test(part)) {
        return part;
      }
      // Capitalize first letter
      return part.charAt(0).toUpperCase() + part.slice(1).toLowerCase();
    })
    .join(' ');
};

/**
 * Generate SEO-friendly title from search parameters
 */
export const generateSEOTitle = (
  params: Partial<SearchParams>,
  intent: ListingIntent | 'Any' = 'Any'
): string => {
  const parts: string[] = [];

  // Add property type if specified
  if (params.propertyType && params.propertyType !== 'Any') {
    parts.push(`${params.propertyType}s`);
  } else {
    parts.push('Properties');
  }

  // Add intent
  if (intent === 'sale') {
    parts.push('for Sale');
  } else if (intent === 'share') {
    parts.push('to Share');
  } else {
    parts.push('for Rent');
  }

  // Add location
  if (params.location) {
    parts.push(`in ${params.location}`);
  }

  // Add bedroom count
  if (params.bedrooms && params.bedrooms > 0) {
    parts.push(`${params.bedrooms} ${params.bedrooms === 1 ? 'Bed' : 'Beds'}`);
  }

  // Add price range
  if (params.minPrice && params.maxPrice) {
    parts.push(`€${params.minPrice.toLocaleString()} - €${params.maxPrice.toLocaleString()}`);
  }

  parts.push('| RentSmart.ie');

  return parts.join(' ');
};

/**
 * Generate SEO-friendly meta description
 */
export const generateSEODescription = (
  params: Partial<SearchParams>,
  intent: ListingIntent | 'Any' = 'Any'
): string => {
  const parts: string[] = [];

  // Start with intro
  parts.push('Discover');

  // Add property type
  if (params.propertyType && params.propertyType !== 'Any') {
    parts.push(params.propertyType.toLowerCase() + 's');
  } else {
    parts.push('properties');
  }

  // Add intent and location
  if (intent === 'sale') {
    parts.push('for sale');
  } else if (intent === 'share') {
    parts.push('to share');
  } else {
    parts.push('to rent');
  }

  if (params.location) {
    parts.push(`in ${params.location}`);
  }

  const description = parts.join(' ') + '.';

  // Add filter details
  const filterDetails: string[] = [];

  if (params.bedrooms && params.bedrooms > 0) {
    filterDetails.push(`${params.bedrooms} bedroom${params.bedrooms > 1 ? 's' : ''}`);
  }

  if (params.bathrooms && params.bathrooms > 0) {
    filterDetails.push(`${params.bathrooms} bathroom${params.bathrooms > 1 ? 's' : ''}`);
  }

  if (params.minPrice && params.maxPrice) {
    filterDetails.push(
      `€${params.minPrice.toLocaleString()} - €${params.maxPrice.toLocaleString()}`
    );
  }

  if (filterDetails.length > 0) {
    return `${description} Find ${filterDetails.join(', ')} on Ireland's #1 property marketplace.`;
  }

  return `${description} Search from thousands of listings on Ireland's #1 property marketplace - RentSmart.ie`;
};

/**
 * Generate SEO-friendly H1 tag
 */
export const generateSEOH1 = (
  params: Partial<SearchParams>,
  intent: ListingIntent | 'Any' = 'Any'
): string => {
  const parts: string[] = [];

  // Add property type
  if (params.propertyType && params.propertyType !== 'Any') {
    parts.push(params.propertyType);
  }

  // Add intent
  if (intent === 'sale') {
    parts.push('for Sale');
  } else if (intent === 'share') {
    parts.push('to Share');
  } else {
    parts.push('for Rent');
  }

  // Add location
  if (params.location) {
    parts.push(`in ${params.location}`);
  }

  if (parts.length === 0) {
    parts.push('Properties');
  }

  return parts.join(' ');
};

/**
 * Generate canonical URL
 */
export const generateCanonicalUrl = (pathSegments: string[]): string => {
  const baseUrl = 'https://rentsmart.ie';

  if (pathSegments.length === 0) {
    return `${baseUrl}/browse`;
  }

  return `${baseUrl}/${pathSegments.join('/')}`;
};

/**
 * Generate structured data (Schema.org) for search results page
 */
export const generateStructuredData = (params: Partial<SearchParams>, totalResults: number) => {
  return {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: generateSEOH1(params),
    description: generateSEODescription(params),
    url: generateCanonicalUrl([]),
    numberOfItems: totalResults,
    mainEntity: {
      '@type': 'SearchResultsPage',
      potentialAction: {
        '@type': 'SearchAction',
        target: 'https://rentsmart.ie/browse?q={search_term}',
      },
    },
  };
};
